import { FC, useState } from "react";
import classNames from "classnames";
import { format, startOfDay, isSameDay } from "date-fns";
import DatePicker from "react-datepicker";

import OutsideClickHandler from "../OutsideClickHandler/OutsideClickHandler";
import { DatePeriodSelectorProps, DateSelectionMode, OverviewPeriod } from "./types";
import { DateIcon } from "../icons/DateIcon";
import { getDaysAgoDate, thirtyDaysAgo } from "../../store/utils/date/dayAgo";
import { PERIOD_OPTIONS, PERIOD_LABELS } from "./const";
import { PeriodOption } from "./types";

import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/library/react-datepicker.css";
import styles from "./DatePeriodSelector.module.css";



const DatePeriodSelector: FC<DatePeriodSelectorProps> = ({
  period,
  selectedDate,
  mode,
  isPeriodEnabled = true,
  onChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const displayValue =
    mode === DateSelectionMode.Period && isPeriodEnabled
      ? PERIOD_LABELS[period]
      : isSameDay(selectedDate, startOfDay(new Date()))
        ? PERIOD_LABELS[OverviewPeriod.OneDay]
        : format(selectedDate, "MMMM d, yyyy");

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleOutsideClick = () => setIsOpen(false);

  const handlePeriodClick = (option: PeriodOption) => {
    if (option.period === OverviewPeriod.OneDay) {
      const today = startOfDay(new Date());
      onChange?.({
        type: DateSelectionMode.Date,
        dateTs: today.getTime(),
      });
    } else {
      onChange?.({
        type: DateSelectionMode.Period,
        period: option.period,
        startTs: startOfDay(getDaysAgoDate(option.days)).getTime(),
      });
    }
    setIsOpen(false);
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) return;

    onChange?.({
      type: DateSelectionMode.Date,
      dateTs: date.getTime(),
    });

    setIsOpen(false);
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div
        className={classNames(styles["date-period-selector"], className, {
          [styles["is-open"]]: isOpen,
        })}
      >
        <button
          type="button"
          className={styles["date-period-selector__trigger"]}
          onClick={handleToggle}
        >
          <div className={styles["date-period-selector__trigger-content"]}>{displayValue}</div>
          <DateIcon className={styles["date-period-selector__icon"]} />
        </button>

        {isOpen && (
          <div className={styles["date-period-selector__dropdown"]}>
            {isPeriodEnabled && (
              <div className={styles["date-period-selector__period-list"]}>
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.period}
                    type="button"
                    className={classNames(styles["date-period-selector__period-item"], {
                      [styles["is-selected"]]: mode === DateSelectionMode.Period && period === option.period,
                    })}
                    onClick={() => handlePeriodClick(option)}
                  >
                    {PERIOD_LABELS[option.period]}
                  </button>
                ))}
              </div>
            )}
            <div className={styles["date-period-selector__calendar"]}>
              <DatePicker
                selected={mode === DateSelectionMode.Date ? selectedDate : null}
                onChange={handleDateChange}
                inline
                minDate={thirtyDaysAgo}
                maxDate={new Date()}
              />
            </div>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default DatePeriodSelector;
